import React, { useState, useEffect, useRef } from 'react'
import { withTranslation } from 'react-i18next'
import {
  Box,
  Alert,
  Typography,
  Input,
  Button,
  Link,
  Checkbox,
  FormControlLabel,
  IconButton,
  CircularProgress
} from '@mui/material'

import { useMutation, gql } from '@apollo/client'
import { ownerLogin, influencerLogin } from '../apollo'
import { validateFunc } from '../constraints/constraints'
import useGlobalStyles from '../utils/globalStyles'
import BorkuMark from '../assets/img/borkuIcon.png'
import InputAdornment from '@mui/material/InputAdornment'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined'

const LOGIN = gql`
  ${ownerLogin}
`
const INFLUENCER_LOGIN = gql`
  ${influencerLogin}
`
const Login = props => {
  const [showPassword, setShowPassword] = useState(false)
  const [stateData, setStateData] = useState({
    email: '',
    password: '',
    emailError: null,
    passwordError: null,
    error: null,
    type: null, /// 0 for vendor
    redirectToReferrer: !!localStorage.getItem('user-borku')
  })
  const formRef = useRef()
  const { t } = props

  const [isLogged, setIsLogged] = useState(false)
  const onBlur = (event, field) => {
    setStateData({
      ...stateData,
      [field + 'Error']: !validateFunc({ [field]: stateData[field] }, field)
    })
  }
  const validate = () => {
    const emailError = !validateFunc({ email: stateData.email }, 'email')
    const passwordError = !validateFunc(
      { password: stateData.password },
      'password'
    )
    setStateData({ ...stateData, emailError, passwordError })
    return emailError && passwordError
  }
  const { redirectToReferrer, type } = stateData

  useEffect(() => {
    if (isLogged) {
      if (redirectToReferrer && type === 0) {
        props.history.replace('/restaurant/list')
      }
      if (redirectToReferrer && type === 1) {
        props.history.replace('/super_admin/dashboard')
      }
      if (redirectToReferrer && type === 2) {
        props.history.replace('/influencer/home')
      }
    }
  }, [isLogged])

  const onCompleted = data => {
    localStorage.setItem('user-borku', JSON.stringify(data.ownerLogin))
    const userType = data.ownerLogin.userType
    if (userType === 'VENDOR') {
      setStateData({
        ...stateData,
        redirectToReferrer: true,
        type: 0,
        emailError: null,
        passwordError: null
      })
    } else {
      setStateData({
        ...stateData,
        redirectToReferrer: true,
        type: 1,
        emailError: null,
        passwordError: null
      })
    }
    setIsLogged(true)
    setTimeout(hideAlert, 5000)
  }
  const hideAlert = () => {
    setStateData({
      ...stateData,
      emailError: null,
      passwordError: null
    })
  }
  const showError = error => {
    if (error.graphQLErrors && error.graphQLErrors.length) {
      setStateData({
        ...stateData,
        error: error.graphQLErrors[0].message
      })
    } else {
      setStateData({
        ...stateData,
        error: error.message
      })
    }
    setIsLogged(false)
    setTimeout(hideAlert, 5000)
  }

  const onInfluencerCompleted = data => {
    localStorage.setItem('user-borku', JSON.stringify(data.influencerLogin))
    setStateData({
      ...stateData,
      redirectToReferrer: true,
      type: 2,
      emailError: null,
      passwordError: null
    })
    setIsLogged(true)
    setTimeout(hideAlert, 5000)
  }

  const [influencerMutate, { loading: influencerLoading }] = useMutation(
    INFLUENCER_LOGIN,
    {
      onError: showError,
      onCompleted: onInfluencerCompleted
    }
  )

  // Owners and influencers share one login box. Influencers are not Owner
  // records, so a failed owner login falls through to the influencer check
  // before the error is shown.
  const onError = error => {
    influencerMutate({
      variables: { email: stateData.email, password: stateData.password }
    }).catch(() => showError(error))
  }
  const [mutate, { loading: ownerLoading }] = useMutation(LOGIN, {
    onError,
    onCompleted
  })

  const loggingIn = ownerLoading || influencerLoading
  const loginFunc = async () => {
    if (validate()) {
      mutate({ variables: { ...stateData } })
    }
  }

  const globalClasses = useGlobalStyles()
  const fieldState = valid =>
    valid === false
      ? globalClasses.inputError
      : valid === true
      ? globalClasses.inputSuccess
      : ''
  const onSubmit = e => {
    e.preventDefault()
    if (!loggingIn) loginFunc()
  }

  const features = [
    {
      icon: ReceiptLongOutlinedIcon,
      title: t('Live orders'),
      text: t('Track every order from checkout to doorstep.')
    },
    {
      icon: StorefrontOutlinedIcon,
      title: t('Stores & vendors'),
      text: t('Onboard vendors and manage their stores.')
    },
    {
      icon: InsightsOutlinedIcon,
      title: t('Clear reporting'),
      text: t('Sales, payouts and trends at a glance.')
    }
  ]

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1.1fr 1fr' },
        bgcolor: '#F7F7F9'
      }}>
      {/* Brand panel */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          position: 'relative',
          overflow: 'hidden',
          m: 2,
          p: { md: 5, lg: 7 },
          borderRadius: '28px',
          color: '#fff',
          background:
            'radial-gradient(120% 90% at 0% 0%, #FF9A5A 0%, #F4661F 45%, #C8410B 100%)'
        }}>
        {/* soft decorative shapes */}
        <Box
          sx={{
            position: 'absolute',
            width: 420,
            height: 420,
            borderRadius: '50%',
            right: -140,
            top: -120,
            background: 'rgba(255,255,255,0.10)'
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: 300,
            height: 300,
            borderRadius: '50%',
            left: -100,
            bottom: -110,
            background: 'rgba(255,255,255,0.08)'
          }}
        />

        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5
          }}>
          {/* White plate so the orange logo stands out on the orange panel */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 2.5,
              py: 1.5,
              borderRadius: '18px',
              bgcolor: 'rgba(255,255,255,0.95)',
              boxShadow: '0 10px 30px rgba(120,40,0,0.25)'
            }}>
            <Box
              component="img"
              src={BorkuMark}
              alt="Borku"
              sx={{
                width: { md: 150, lg: 180 },
                height: 'auto',
                objectFit: 'contain',
                display: 'block'
              }}
            />
          </Box>
        </Box>

        <Box sx={{ position: 'relative', maxWidth: 480 }}>
          <Typography
            sx={{
              fontSize: { md: 32, lg: 40 },
              fontWeight: 800,
              lineHeight: 1.1,
              letterSpacing: '-0.02em'
            }}>
            {t(
              'Run Your Business from One Place Manage everything with Borku Partner Hub.'
            )}
          </Typography>
          <Typography sx={{ fontSize: 17, opacity: 0.9, mt: 2 }}>
            {t(
              'Manage orders, stores, riders and payouts all in one place, giving you everything you need to run and grow your business efficiently.'
            )}
          </Typography>

          <Box sx={{ mt: 5, display: 'grid', gap: 1.5 }}>
            {features.map(({ icon: Icon, title, text }) => (
              <Box
                key={title}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  borderRadius: '16px',
                  bgcolor: 'rgba(255,255,255,0.12)',
                  border: '1px solid rgba(255,255,255,0.22)',
                  backdropFilter: 'blur(8px)'
                }}>
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                    bgcolor: '#fff',
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                  <Icon />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
                    {title}
                  </Typography>
                  <Typography sx={{ fontSize: 14, opacity: 0.85 }}>
                    {text}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Typography sx={{ position: 'relative', fontSize: 13, opacity: 0.8 }}>
          © {new Date().getFullYear()} Borku
        </Typography>
      </Box>

      {/* Sign-in form */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2.5, sm: 4 },
          py: 6
        }}>
        <Box sx={{ width: '100%', maxWidth: 440 }}>
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              alignItems: 'center',
              gap: 1.25,
              mb: 4
            }}>
            <Box
              component="img"
              src={BorkuMark}
              alt=""
              sx={{ width: 44, height: 44, objectFit: 'contain' }}
            />
            <Typography
              sx={{ fontSize: 22, fontWeight: 800, letterSpacing: 1 }}>
              BORKU
            </Typography>
          </Box>

          <Typography
            component="h1"
            sx={{
              fontSize: { xs: 28, sm: 28 },
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: 'text.primary'
            }}>
            {t('Welcome to Borku Partner Hub')}
          </Typography>
          <Typography sx={{ fontSize: 16, color: 'text.disabled', mt: 1 }}>
            {t('Sign in to your Borku account.')}
          </Typography>

          <Box
            component="form"
            ref={formRef}
            onSubmit={onSubmit}
            noValidate
            sx={{ mt: 4 }}>
            <Typography sx={labelSx}>{t('Email')}</Typography>
            <Input
              id="input-email"
              name="input-email"
              value={stateData.email}
              onChange={event => {
                setStateData({ ...stateData, email: event.target.value })
              }}
              onBlur={event => {
                onBlur(event, 'email')
              }}
              placeholder={t('you@borku.co.uk')}
              type="email"
              autoComplete="email"
              autoFocus
              disableUnderline
              className={[
                globalClasses.input,
                fieldState(stateData.emailError)
              ]}
              sx={inputSx}
              startAdornment={
                <InputAdornment position="start">
                  <MailOutlineRoundedIcon sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              }
            />
            {stateData.emailError === false ? (
              <Typography sx={{ color: 'error.main', fontSize: 13, mt: 0.75 }}>
                {t('Enter a valid email address.')}
              </Typography>
            ) : null}

            <Typography sx={{ ...labelSx, mt: 2.5 }}>
              {t('Password')}
            </Typography>
            <Input
              id="input-password"
              name="input-password"
              placeholder={t('Enter your password')}
              value={stateData.password}
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              onChange={event => {
                setStateData({ ...stateData, password: event.target.value })
              }}
              onBlur={event => {
                onBlur(event, 'password')
              }}
              disableUnderline
              className={[
                globalClasses.input,
                fieldState(stateData.passwordError)
              ]}
              sx={inputSx}
              startAdornment={
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              }
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    size="small"
                    aria-label={t('Toggle password visibility')}
                    onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? (
                      <VisibilityIcon fontSize="small" />
                    ) : (
                      <VisibilityOffIcon fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              }
            />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                mt: 2
              }}>
              <FormControlLabel
                control={<Checkbox defaultChecked size="small" />}
                label={t('RememberMe')}
                sx={{ '& .MuiFormControlLabel-label': { fontSize: 14 } }}
              />
              <Link
                href="/#/auth/reset"
                underline="hover"
                sx={{ color: 'primary.main', fontWeight: 600, fontSize: 14 }}>
                {t('ForgotYourPassword')}
              </Link>
            </Box>

            {stateData.error ? (
              <Alert severity="error" sx={{ mt: 2, borderRadius: '12px' }}>
                {stateData.error}
              </Alert>
            ) : null}

            <Button
              type="submit"
              fullWidth
              disabled={loggingIn}
              endIcon={
                loggingIn ? null : <ArrowForwardRoundedIcon fontSize="small" />
              }
              sx={{
                mt: 3,
                height: 52,
                borderRadius: '14px',
                bgcolor: 'primary.main',
                color: '#fff',
                fontSize: 16,
                fontWeight: 700,
                textTransform: 'none',
                boxShadow: '0 10px 24px rgba(244,102,31,0.28)',
                '&:hover': {
                  bgcolor: 'primary.main',
                  filter: 'brightness(0.95)'
                },
                '&.Mui-disabled': {
                  bgcolor: 'primary.main',
                  color: '#fff',
                  opacity: 0.7
                }
              }}>
              {loggingIn ? (
                <CircularProgress size={22} sx={{ color: '#fff' }} />
              ) : (
                t('Sign in')
              )}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

const labelSx = {
  fontSize: 14,
  fontWeight: 600,
  color: 'text.primary',
  mb: 0.75
}
const inputSx = {
  height: '54px !important',
  borderRadius: '14px !important',
  bgcolor: '#fff',
  mt: '0 !important'
}

export default withTranslation()(Login)
